/**
 * Sammel-Statuswechsel - Auftrag "Auf der Bestellübersicht Sammeländerungen für Status und
 * Bereich" (Asana), STATUS-Haelfte. Kein Bestandsvorbild: der Bestand kennt keine Mehrfachauswahl
 * und damit keinen Sammeldialog dafuer - NEUBAU, wie `JtlLoeschDialog.tsx`/`ListenEditor.tsx`.
 *
 * ================= Wie diese Komponente eingehaengt wird =================
 *
 * Ausgeloest werden kann derselbe Dialog von ZWEI Stellen (Entwurfsentscheidung "Beides anbieten"):
 * dem Werkzeugleiste-Knopf „Status ändern ({N})" (`Werkzeugleiste.tsx`, `BoardSeite.tsx`s Prop
 * `onSammelStatusAnfordern`) UND dem neuen Kontextmenue-Eintrag „Status ändern für {N}
 * Bestellungen…" (`KontextMenue.tsx`, Aktion `"bulkStatus"`). Beide Wege setzen in `App.tsx` nur
 * `sammelStatusDialogOffen` auf `true` - die eigentliche Auswahl (welche Ids gemeint sind) liegt
 * bereits in `App.tsx`s `ausgewaehlteBestellungen` (siehe dortiger Kopfkommentar), diese Komponente
 * kennt die Ids selbst NICHT, nur die ANZAHL (`anzahl`, fuer den Titel).
 *
 * Wie `KontextMenue`/`ListenEditor` eine EXTERNE Naht: `App.tsx` mountet sie UNVERAENDERT (kein
 * bedingtes `{sammelStatusDialogOffen ? <SammelStatusDialog .../> : null}` wie bei `ListenEditor` -
 * stattdessen IMMER im Baum, `offen` entscheidet ueber das Rendern INNERHALB der Komponente,
 * dieselbe Bauart wie `KontextMenue`s `bestellung: null` - siehe Annahme unten). `definitionen`
 * (die Statusliste) reicht `App.tsx` durch, weil sie dort ohnehin fuer alle Statusanzeigen zentral
 * berechnet wird (`setStatusDefinitions`, siehe Auftragsbrief).
 *
 * ================= Aufbau =================
 *
 * Portal auf `document.body`, dieselben `board-editor-*`-Klassen (`bestand.css`) wie
 * `Drawer/JtlLoeschDialog.tsx` - "das bereits vorhandene generische Dialog-/Overlay-Muster", statt
 * etwas Neues zu erfinden (Auftragsbrief). Escape UND ein Klick ausserhalb schliessen: der
 * Aussenklick laeuft wie bei `JtlLoeschDialog`/`ListenEditor` ueber den Overlay-`onClick`
 * (`target === currentTarget`), Escape wie bei `KontextMenue.tsx` (Zeilen 132-149) ueber einen
 * eigenen `document`-Listener, der nur lebt, waehrend `offen` gilt.
 *
 * Die Statusliste (Dropdown) ist dieselbe Quelle wie der bestehende Zelleneditor der Bestellliste
 * (`Bestellzeile.tsx#ZellenEditor`, `regel.art === "status"`): `definitionen.STATUSES`, `key`/
 * `value` = `status.id`, Beschriftung `status.label` - keine zweite Statusliste.
 *
 * ================= Annahme =================
 *
 * Wie `KontextMenue` rendert diese Komponente NICHTS, wenn `!offen` (`return null`) - es gibt dafuer
 * keine Baseline (NEUBAU ohne Bestandsvorbild), ein Teilbaumvergleich entfaellt also ohnehin.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { StatusDefinitionen } from "../../lib/status.ts";
import { Ikone } from "../Ikone.tsx";

export interface SammelStatusDialogProps {
  /** `false`/fehlend: die Komponente rendert nichts, siehe Kopfkommentar, Annahme. */
  offen: boolean;
  /** Groesse der aktuellen Auswahl - fuer den Titel „Status ändern für {anzahl} Bestellungen". */
  anzahl: number;
  /** Dieselbe Quelle wie der Zelleneditor der Bestellliste (`Bestellzeile.tsx`). */
  definitionen: StatusDefinitionen;
  /** Klick auf „Übernehmen" mit dem im Dropdown gewaehlten Status. */
  onUebernehmen: (status: string) => void;
  /** Abbrechen-Knopf, Escape oder Klick ausserhalb - alle drei denselben Weg. */
  onAbbrechen: () => void;
  /**
   * NACHBESSERUNG 2 (Pruefung 97e5bffb, Gering): `true`, waehrend `App.tsx#sammelStatusAendern`
   * unterwegs ist (vom Klick auf „Übernehmen" bis der Dialog schliesst bzw. der Fehlschlag
   * protokolliert ist). Sperrt NUR den „Übernehmen"-Knopf (siehe dort) - ein zweiter Klick waehrend
   * des laufenden Rundlaufs (Schreiben + N Nachlade-GETs) loeste sonst einen zweiten Bulk-Request mit
   * inzwischen veralteten `rowVersion`-Werten aus. „Abbrechen" bleibt bewusst bedienbar: der Dialog
   * selbst schliesst dadurch trotzdem noch, unabhaengig vom laufenden Request.
   */
  laeuft?: boolean;
}

export function SammelStatusDialog({
  offen,
  anzahl,
  definitionen,
  onUebernehmen,
  onAbbrechen,
  laeuft,
}: SammelStatusDialogProps) {
  // Vorgabe: der erste Eintrag der Statusliste - dieselbe Erwartung wie ein natives <select> ohne
  // eigenes value/onChange (der Browser waehlt sonst ebenfalls die erste <option>).
  const [status, setStatus] = useState<string>(() => String(definitionen.STATUSES[0]?.id ?? ""));

  // Escape schliesst - Muster `KontextMenue.tsx`, Zeilen 132-149: der Listener lebt nur, waehrend
  // der Dialog offen ist.
  useEffect(() => {
    if (!offen) return;
    const beiTaste = (ereignis: KeyboardEvent): void => {
      if (ereignis.key === "Escape") onAbbrechen();
    };
    document.addEventListener("keydown", beiTaste);
    return () => document.removeEventListener("keydown", beiTaste);
  }, [offen, onAbbrechen]);

  // Bei jedem erneuten Oeffnen wieder auf den ersten Status zuruecksetzen - sonst traegt ein zweiter
  // Aufruf noch die Auswahl des vorherigen (anderen) Sammelvorgangs weiter.
  useEffect(() => {
    if (offen) setStatus(String(definitionen.STATUSES[0]?.id ?? ""));
  }, [offen, definitionen]);

  if (!offen) return null;

  return createPortal(
    <div
      className="board-editor-overlay open"
      role="presentation"
      onClick={(ereignis) => {
        if (ereignis.target === ereignis.currentTarget) onAbbrechen();
      }}
    >
      <section
        className="board-editor-dialog sammel-status-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sammelStatusTitel"
      >
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="sammelStatusTitel">{`Status ändern für ${anzahl} ${
              anzahl === 1 ? "Bestellung" : "Bestellungen"
            }`}</h2>
          </div>
          <button className="icon-btn" type="button" onClick={onAbbrechen} title="Schließen">
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-body">
          <label>
            Neuer Status
            <select value={status} onChange={(ereignis) => setStatus(ereignis.target.value)}>
              {definitionen.STATUSES.map((eintrag) => (
                <option key={eintrag.id as string} value={eintrag.id as string}>
                  {eintrag.label as string}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onAbbrechen}>
            Abbrechen
          </button>
          <button
            className="btn-primary"
            type="button"
            disabled={!status || !!laeuft}
            onClick={() => onUebernehmen(status)}
          >
            Übernehmen
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
